// src/pages/Services.jsx

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

import {
  FaArrowRight,
  FaCheck,
  FaWhatsapp,
  FaCode,
  FaMobileAlt,
  FaBullhorn,
  FaVideo,
  FaUsers,
  FaRocket,
  FaLightbulb,
} from "react-icons/fa";

const SERVICES = [
  {
    number: "01",
    title: "Web Development",
    shortTitle: "Web",
    description:
      "Modern, responsive websites and web applications designed to build trust, showcase your business and generate enquiries.",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=85",
    icon: <FaCode />,
    accent: "blue",
    solutions: [
      {
        title: "Business Websites",
        who: "Salons · Clinics · Local Businesses",
      },
      {
        title: "E-Commerce Websites",
        who: "Stores · Clothing · Jewellery",
      },
      {
        title: "Booking Websites",
        who: "Salons · Clinics · Service Businesses",
      },
      {
        title: "Restaurant Websites",
        who: "Restaurants · Cafés · Food Businesses",
      },
      {
        title: "Real Estate Websites",
        who: "Agencies · Property Consultants",
      },
      {
        title: "Custom Web Applications",
        who: "Startups · Growing Businesses",
      },
    ],
  },

  {
    number: "02",
    title: "Mobile App Development",
    shortTitle: "Mobile Apps",
    description:
      "Scalable mobile applications built around your customers, services and business operations for Android and modern digital experiences.",
    image:
      "https://images.unsplash.com/photo-1551650975-87deedd944c3?auto=format&fit=crop&w=1200&q=85",
    icon: <FaMobileAlt />,
    accent: "cyan",
    solutions: [
      {
        title: "Business Mobile Apps",
        who: "Retail · Service Companies",
      },
      {
        title: "E-Commerce Apps",
        who: "Online Stores · Brands",
      },
      {
        title: "Food Delivery Apps",
        who: "Restaurants · Cloud Kitchens",
      },
      {
        title: "Booking Apps",
        who: "Salons · Home Services",
      },
      {
        title: "Healthcare Apps",
        who: "Hospitals · Clinics",
      },
      {
        title: "Custom Business Apps",
        who: "Startups · Growing Companies",
      },
    ],
  },

  {
    number: "03",
    title: "Digital Marketing",
    shortTitle: "Marketing",
    description:
      "Focused digital marketing strategies designed to improve visibility, engagement, brand presence and qualified lead generation.",
    image:
      "https://images.unsplash.com/photo-1557838923-2985c318be48?auto=format&fit=crop&w=1200&q=85",
    icon: <FaBullhorn />,
    accent: "violet",
    solutions: [
      {
        title: "Social Media Marketing",
        who: "Restaurants · Salons · Brands",
      },
      {
        title: "Google Ads",
        who: "Real Estate · Clinics · Local Businesses",
      },
      {
        title: "SEO",
        who: "Hotels · Local Service Businesses",
      },
      {
        title: "Lead Generation",
        who: "Real Estate · Education · Services",
      },
      {
        title: "Google Business Profile",
        who: "Restaurants · Shops · Local Businesses",
      },
      {
        title: "WhatsApp Marketing",
        who: "Retail · Service Businesses",
      },
      {
        title: "Meta Ads",
        who: "Retail · Service Businesses",
      },
    ],
  },

  {
    number: "04",
    title: "Video & AI Services",
    shortTitle: "Video & AI",
    description:
      "Professional video editing and AI-powered content designed to help businesses create engaging promotional and social media content.",
    image:
      "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?auto=format&fit=crop&w=1200&q=85",
    icon: <FaVideo />,
    accent: "pink",
    solutions: [
      {
        title: "Business Promotional Videos",
        who: "Restaurants · Real Estate",
      },
      {
        title: "Reels & Short Videos",
        who: "Fashion · Beauty · Local Brands",
      },
      {
        title: "Product Videos",
        who: "E-Commerce · Jewellery Brands",
      },
      {
        title: "AI Marketing Videos",
        who: "Small Businesses · Startups",
      },
      {
        title: "AI Avatar Videos",
        who: "Companies · Coaching Institutes",
      },
      {
        title: "AI Voiceover & Dubbing",
        who: "YouTubers · Businesses",
      },
    ],
  },

  {
    number: "05",
    title: "CRM Development",
    shortTitle: "CRM",
    description:
      "Custom CRM and business management systems that organize customers, sales, appointments and daily business operations.",
    image:
      "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1200&q=85",
    icon: <FaUsers />,
    accent: "emerald",
    solutions: [
      {
        title: "Custom CRM",
        who: "Sales · Real Estate Businesses",
      },
      {
        title: "Lead Management",
        who: "Agencies · Education · Sales",
      },
      {
        title: "Customer Management",
        who: "Salons · Service Companies",
      },
      {
        title: "Sales Management",
        who: "Wholesale · Sales Teams",
      },
      {
        title: "Appointment Management",
        who: "Clinics · Salons",
      },
      {
        title: "Business Management Systems",
        who: "Growing · Multi-Branch Businesses",
      },
    ],
  },
];

const ACCENTS = {
  blue: {
    text: "text-blue-600",
    bg: "bg-blue-50",
    softBg: "bg-blue-50/70",
    border: "border-blue-100",
    hoverBorder: "hover:border-blue-300",
    gradient: "from-blue-600 to-cyan-500",
    glow: "group-hover:shadow-blue-100",
  },
  cyan: {
    text: "text-cyan-600",
    bg: "bg-cyan-50",
    softBg: "bg-cyan-50/70",
    border: "border-cyan-100",
    hoverBorder: "hover:border-cyan-300",
    gradient: "from-cyan-500 to-blue-500",
    glow: "group-hover:shadow-cyan-100",
  },
  violet: {
    text: "text-violet-600",
    bg: "bg-violet-50",
    softBg: "bg-violet-50/70",
    border: "border-violet-100",
    hoverBorder: "hover:border-violet-300",
    gradient: "from-violet-600 to-blue-500",
    glow: "group-hover:shadow-violet-100",
  },
  pink: {
    text: "text-pink-600",
    bg: "bg-pink-50",
    softBg: "bg-pink-50/70",
    border: "border-pink-100",
    hoverBorder: "hover:border-pink-300",
    gradient: "from-pink-500 to-violet-500",
    glow: "group-hover:shadow-pink-100",
  },
  emerald: {
    text: "text-emerald-600",
    bg: "bg-emerald-50",
    softBg: "bg-emerald-50/70",
    border: "border-emerald-100",
    hoverBorder: "hover:border-emerald-300",
    gradient: "from-emerald-500 to-cyan-500",
    glow: "group-hover:shadow-emerald-100",
  },
};

export default function Services() {
  useEffect(() => {
    AOS.init({
      duration: 750,
      once: true,
      easing: "ease-out-cubic",
      offset: 70,
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <main className="min-h-screen overflow-hidden bg-white font-[Manrope] text-slate-900">
      {/* =====================================================
          HERO
      ====================================================== */}

      <section className="relative overflow-hidden px-5 pb-16 pt-32 sm:px-8 lg:px-10 lg:pb-20">
        {/* Background */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-40 top-0 h-96 w-96 rounded-full bg-blue-100/60 blur-3xl" />

          <div className="absolute -right-40 top-10 h-[420px] w-[420px] rounded-full bg-cyan-100/50 blur-3xl" />

          <div className="absolute left-1/2 top-32 h-40 w-40 -translate-x-1/2 rounded-full bg-violet-100/30 blur-3xl" />

          <div
            className="absolute inset-0 opacity-[0.035]"
            style={{
              backgroundImage:
                "linear-gradient(#2563eb 1px, transparent 1px), linear-gradient(90deg, #2563eb 1px, transparent 1px)",
              backgroundSize: "64px 64px",
            }}
          />
        </div>

        <div
          className="relative mx-auto max-w-5xl text-center"
          data-aos="fade-up"
        >
          {/* Badge */}
          <span className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-5 py-2.5 text-xs font-extrabold tracking-[0.2em] text-blue-600 shadow-sm">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600" />
            </span>
            OUR SERVICES
          </span>

          {/* Heading */}
          <h1 className="mx-auto mt-7 max-w-4xl text-4xl font-extrabold leading-[1.08] tracking-[-0.04em] text-slate-950 sm:text-5xl lg:text-6xl">
            Digital Solutions
            <span className="mt-2 block bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-600 bg-clip-text text-transparent">
              Built For Growth
            </span>
          </h1>

          <p className="mx-auto mt-6 max-w-2xl text-base leading-8 text-slate-500 sm:text-lg">
            From high-converting websites and mobile apps to digital
            marketing, AI content and custom CRM systems — we build digital
            solutions around your business goals.
          </p>

          {/* Quick Navigation */}
          <div className="mt-9 flex flex-wrap justify-center gap-2.5">
            {SERVICES.map((service) => {
              const accent = ACCENTS[service.accent];

              return (
                <a
                  key={service.number}
                  href={`#service-${service.number}`}
                  className={`rounded-full border ${accent.border} ${accent.bg} px-4 py-2.5 text-xs font-bold ${accent.text} shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-md`}
                >
                  {service.number} · {service.shortTitle}
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* =====================================================
          SERVICES
      ====================================================== */}

      <section className="relative px-5 pb-24 sm:px-8 lg:px-10">
        <div className="mx-auto max-w-6xl space-y-8">
          {SERVICES.map((service, index) => {
            const accent = ACCENTS[service.accent];

            return (
              <article
                id={`service-${service.number}`}
                key={service.number}
                data-aos="fade-up"
                data-aos-delay={index * 70}
                className={`group overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_15px_60px_rgba(15,23,42,0.07)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_25px_80px_rgba(37,99,235,0.10)] ${accent.hoverBorder}`}
              >
                <div className="grid lg:grid-cols-[0.72fr_1.28fr]">
                  {/* Image */}
                  <div className="relative min-h-[300px] overflow-hidden lg:min-h-[470px]">
                    <img
                      src={service.image}
                      alt={service.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                    />

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent" />

                    {/* Top number */}
                    <div className="absolute left-6 top-6 flex h-12 w-12 items-center justify-center rounded-2xl border border-white/20 bg-white/15 text-sm font-extrabold text-white backdrop-blur-xl">
                      {service.number}
                    </div>

                    {/* Icon */}
                    <div className="absolute right-6 top-6 flex h-12 w-12 items-center justify-center rounded-2xl border border-white/20 bg-white/15 text-lg text-white backdrop-blur-xl transition duration-300 group-hover:scale-110">
                      {service.icon}
                    </div>

                    {/* Image content */}
                    <div className="absolute bottom-7 left-6 right-6">
                      <span className="text-xs font-extrabold tracking-[0.2em] text-white/60">
                        DIGITAL SOLUTION
                      </span>

                      <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
                        {service.title}
                      </h2>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 sm:p-8 lg:p-10">
                    {/* Heading */}
                    <div className="flex items-start justify-between gap-6">
                      <div>
                        <div
                          className={`text-xs font-extrabold uppercase tracking-[0.2em] ${accent.text}`}
                        >
                          What We Offer
                        </div>

                        <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">
                          {service.title}
                        </h2>

                        <p className="mt-4 max-w-2xl text-base leading-7 text-slate-500 sm:text-lg">
                          {service.description}
                        </p>
                      </div>

                      {/* Desktop icon */}
                      <div
                        className={`hidden h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${accent.bg} ${accent.text} text-xl shadow-sm sm:flex`}
                      >
                        {service.icon}
                      </div>
                    </div>

                    {/* Benefits */}
                    <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3 border-y border-slate-100 py-5">
                      {[
                        "Business-focused",
                        "Modern technology",
                        "Responsive design",
                        "Long-term support",
                      ].map((item) => (
                        <span
                          key={item}
                          className="flex items-center gap-2 text-sm font-semibold text-slate-600"
                        >
                          <span
                            className={`flex h-5 w-5 items-center justify-center rounded-full ${accent.bg}`}
                          >
                            <FaCheck
                              className={`text-[9px] ${accent.text}`}
                            />
                          </span>

                          {item}
                        </span>
                      ))}
                    </div>

                    {/* Sub Services Heading */}
                    <div className="mt-7">
                      <div className="flex items-end justify-between gap-4">
                        <div>
                          <p
                            className={`text-xs font-extrabold uppercase tracking-[0.18em] ${accent.text}`}
                          >
                            Our Expertise
                          </p>

                          <h3 className="mt-1.5 text-xl font-extrabold text-slate-950 sm:text-2xl">
                            Services Included
                          </h3>
                        </div>

                        <span className="hidden text-sm font-bold text-slate-400 sm:block">
                          {service.solutions.length} Solutions
                        </span>
                      </div>

                      {/* Sub Services */}
                      <div className="mt-5 grid gap-3 sm:grid-cols-2">
                        {service.solutions.map((item, solutionIndex) => (
                          <div
                            key={item.title}
                            className={`group/item rounded-2xl border border-slate-100 bg-slate-50/80 p-4 transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-lg ${accent.glow} ${accent.hoverBorder}`}
                          >
                            <div className="flex items-center gap-3.5">
                              {/* Number */}
                              <span
                                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${accent.bg} ${accent.text} text-[10px] font-extrabold transition duration-300 group-hover/item:scale-105`}
                              >
                                {String(solutionIndex + 1).padStart(2, "0")}
                              </span>

                              <div className="min-w-0">
                                <h4 className="text-sm font-extrabold text-slate-900 transition duration-200 group-hover/item:text-blue-600 sm:text-base">
                                  {item.title}
                                </h4>

                                <p className="mt-1 text-xs leading-5 text-slate-400">
                                  {item.who}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* =====================================================
          WHY CHOOSE US
      ====================================================== */}

      <section className="relative overflow-hidden border-y border-slate-100 bg-slate-50 px-5 py-20 sm:px-8 lg:px-10">
        <div className="pointer-events-none absolute -left-32 top-10 h-72 w-72 rounded-full bg-blue-100/50 blur-3xl" />
        <div className="pointer-events-none absolute -right-32 bottom-0 h-80 w-80 rounded-full bg-cyan-100/50 blur-3xl" />

        <div className="relative mx-auto max-w-6xl">
          <div
            className="mx-auto max-w-3xl text-center"
            data-aos="fade-up"
          >
            <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-blue-600">
              WHY PRIMEAPPCONNECT
            </span>

            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">
              More Than Just Digital Services.
            </h2>

            <p className="mt-4 text-base leading-7 text-slate-500 sm:text-lg">
              We combine technology, design and business thinking to create
              digital solutions that are practical, scalable and built for
              real growth.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {[
              {
                icon: <FaLightbulb />,
                title: "Smart Solutions",
                text: "We focus on practical solutions that solve real business problems.",
              },
              {
                icon: <FaCode />,
                title: "Modern Technology",
                text: "Clean, responsive and scalable technology for today's digital world.",
              },
              {
                icon: <FaRocket />,
                title: "Growth Focused",
                text: "Every solution is designed with your customers and business growth in mind.",
              },
            ].map((item, index) => (
              <div
                key={item.title}
                data-aos="fade-up"
                data-aos-delay={index * 80}
                className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-lg text-blue-600">
                  {item.icon}
                </div>

                <h3 className="mt-6 text-xl font-extrabold text-slate-950">
                  {item.title}
                </h3>

                <p className="mt-3 text-sm leading-6 text-slate-500 sm:text-base">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          PROCESS
      ====================================================== */}

      <section className="px-5 py-20 sm:px-8 lg:px-10">
        <div className="mx-auto max-w-6xl">
          <div
            className="mx-auto max-w-3xl text-center"
            data-aos="fade-up"
          >
            <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-blue-600">
              OUR PROCESS
            </span>

            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">
              Simple Process. Clear Results.
            </h2>

            <p className="mt-4 text-base leading-7 text-slate-500">
              From the first conversation to launch and beyond, we keep the
              process clear and focused.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              [
                "01",
                "Understand",
                "We understand your goals, audience and requirements.",
              ],
              [
                "02",
                "Plan",
                "We choose the right technology, structure and strategy.",
              ],
              [
                "03",
                "Build",
                "We design, develop and refine your digital solution.",
              ],
              [
                "04",
                "Grow",
                "We support your product and help you move forward.",
              ],
            ].map(([number, title, text], index) => (
              <div
                key={number}
                data-aos="fade-up"
                data-aos-delay={index * 80}
                className="group relative rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-xl"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-extrabold text-blue-600">
                    {number}
                  </span>

                  <span className="h-px w-12 bg-slate-200 transition group-hover:w-16 group-hover:bg-blue-200" />
                </div>

                <h3 className="mt-7 text-xl font-extrabold text-slate-950">
                  {title}
                </h3>

                <p className="mt-3 text-sm leading-6 text-slate-500">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          CTA
      ====================================================== */}

      <section className="px-5 pb-20 sm:px-8 lg:px-10">
        <div
          data-aos="fade-up"
          className="relative mx-auto max-w-6xl overflow-hidden rounded-[32px] bg-gradient-to-br from-blue-600 via-blue-600 to-cyan-500 px-6 py-14 shadow-2xl shadow-blue-600/20 sm:px-10 sm:py-16"
        >
          {/* Decorative shapes */}
          <div className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />

          <div className="absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-cyan-300/20 blur-3xl" />

          <div className="absolute right-20 top-10 h-2 w-2 rounded-full bg-white/60" />
          <div className="absolute bottom-16 left-24 h-2 w-2 rounded-full bg-white/40" />

          <div className="relative mx-auto max-w-3xl text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-white/20 bg-white/15 text-xl text-white backdrop-blur-md">
              <FaRocket />
            </div>

            <p className="mt-6 text-xs font-extrabold tracking-[0.22em] text-blue-100">
              HAVE A PROJECT IN MIND?
            </p>

            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Let's Build Something Great
            </h2>

            <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-blue-100 sm:text-lg">
              Tell us what you're planning and we'll help you choose the
              right digital solution for your business.
            </p>

            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-2 rounded-xl bg-white px-7 py-3.5 text-sm font-extrabold text-blue-600 transition duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                Start Your Project

                <FaArrowRight className="text-[11px] transition duration-300 group-hover:translate-x-1" />
              </Link>

              <a
                href="https://wa.me/916301898060"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2 rounded-xl border border-white/25 bg-white/10 px-7 py-3.5 text-sm font-extrabold text-white backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:bg-white/15"
              >
                <FaWhatsapp className="text-base transition group-hover:scale-110" />
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}